import { JSX } from 'react'
import { AiFillMedicineBox } from 'react-icons/ai'
import { BsExclamationLg } from 'react-icons/bs'
import { CiSearch, CiWarning } from 'react-icons/ci'
import { useNavigate } from 'react-router-dom'

import { OrderExtendedProfile } from '../api/services/orders/types.ts'
import Badge from '../components/common/Badge/Badge.tsx'
import HoverHint from '../components/common/HoverHint'
import SaleDocumentCheckbox from '../components/orders/SaleDocumentCheckbox.tsx'
import { ShownColumn } from '../pages/orders/Orders.tsx'
import { checkRealizationDateWithCurrent } from './dates.ts'
import { convertToShortDate } from './formatter.ts'
import { linkToProfilePD3 } from './patientProfiles.tsx'
import { formatCurrency } from './payments.ts'

type TableColumn = {
  header: string
  accessor: string
  render?: (data: OrderExtendedProfile) => JSX.Element
  className?: string
  classNameTd?: string
  conditionalClassNameCell?: (data: OrderExtendedProfile) => string // New property for conditional cell styling
  sorted?: boolean
}

export const setOrdersTableColumns = (
  shownColumns?: { key: keyof OrderExtendedProfile; label: string }[]
) => {

  const makeGetColumnLabelFromObjects = <T,>(
    shownColumns?: ReadonlyArray<ShownColumn<T>>
  ): ((
    key: keyof T,
    defaultLabel?: string | ((key: keyof T) => string)
  ) => string) => {
    if (!shownColumns || shownColumns.length === 0) {
      return (key, defaultLabel) =>
        typeof defaultLabel === 'function'
          ? defaultLabel(key)
          : (defaultLabel ?? '')
    }

    const labelsByKey = new Map<keyof T, string>(
      shownColumns.map((c) => [c.key, c.label])
    )

    return (key, defaultLabel) => {
      const label = labelsByKey.get(key)
      if (label != null) return label

      return typeof defaultLabel === 'function'
        ? defaultLabel(key)
        : (defaultLabel ?? '')
    }
  }

  function makeIsColumnShownFromObjects<T>(
    shownColumns?: ReadonlyArray<ShownColumn<T>>
  ): (key: keyof T) => boolean {
    if (!shownColumns || shownColumns.length === 0) return () => true
    const allowed = new Set<keyof T>(shownColumns.map((c) => c.key))
    return (key) => allowed.has(key)
  }

  const getLabel =
    makeGetColumnLabelFromObjects<OrderExtendedProfile>(shownColumns)

  const existsColumnInList =
    makeIsColumnShownFromObjects<OrderExtendedProfile>(shownColumns)

  const columns: TableColumn[] = []

  columns.push({
    header: '',
    accessor: 'message',
    render: (row: OrderExtendedProfile) => {
      const expiredStatus = checkRealizationDateWithCurrent(
        row.diet_start_date,
        row.status
      )
      return (
        <>
          {expiredStatus === 'overdue' && (
            <BsExclamationLg size={40} className="text-red-500" />
          )}
          {expiredStatus === 'today' && (
            <CiWarning size={40} className="text-yellow-500" />
          )}
        </>
      )
    },
    className: 'w-[20px]'
  })

  existsColumnInList('id') &&
    columns.push({
      header: getLabel('id','Nr zam'),
      accessor: 'id',
      render: (row: OrderExtendedProfile) => (
        <span>
          {row.id}
        </span>
      ),
      className: 'text-center w-[55px]'
    })

  existsColumnInList('created_at') &&
    columns.push({
      header: getLabel('created_at', 'Data utworzenia'),
      accessor: 'created_at',
      render: (row: OrderExtendedProfile) => (
        <>{convertToShortDate(row.created_at)}</>
      ),
      className: 'w-[120px] 2xl:w-auto',
      sorted: true
    })

  existsColumnInList('diet_start_date') &&
    columns.push({
      header: getLabel('diet_start_date','Start diety'),
      accessor: 'diet_start_date',
      render: (row: OrderExtendedProfile) => <>{row.diet_start_date}</>,
      className: 'w-[120px] 2xl:w-auto',
      conditionalClassNameCell: (row: OrderExtendedProfile) => {
        const expiredStatus = checkRealizationDateWithCurrent(
          row.diet_start_date,
          row.status
        )
        if (expiredStatus === 'overdue') return 'font-bold text-red-500'
        if (expiredStatus === 'today') return 'font-bold text-yellow-500'
        return ''
      },
      sorted: true
    })

  existsColumnInList('last_name' as keyof OrderExtendedProfile) &&
    columns.push({
      header: getLabel('last_name' as keyof OrderExtendedProfile,'Imię i nazwisko'),
      accessor: 'first_name_or_cached',
      render: (row: OrderExtendedProfile) => (
        <>{`${row.patient_profile?.first_name} ${row.patient_profile?.last_name}`}</>
      ),
      className: 'w-[150px] 2xl:w-auto'
    })

  existsColumnInList('subscription') &&
    columns.push({
      header: '',
      accessor: 'subscription',
      render: (row: OrderExtendedProfile) =>
        !!row.subscription ? (
          <HoverHint hintLabel={'Subskrypcja'}>
            <p className="inline-flex px-2 py-1 rounded-xl text-sm bg-blue-400 text-white">
              S
            </p>
          </HoverHint>
        ) : (
          <p></p>
        ),
      className: 'w-[24px] text-center'
    })

  existsColumnInList('status') &&
    columns.push({
      header: getLabel('status','Status'),
      accessor: 'status',
      render: (row: OrderExtendedProfile) => <Badge status={row.status} />,
      className: 'w-[250px] 2xl:w-auto'
    })

  existsColumnInList('subscription_length') &&
    columns.push({
      header: getLabel('subscription_length','Okres'),
      accessor: 'subscription_length',
      render: (row: OrderExtendedProfile) => (
        <span>
          {row.subscription_length * 30}
          {' dni'}
        </span>
      ),
      className: 'text-center w-[100px]'
    })

  existsColumnInList('sale_document_created') &&
    columns.push({
      header: getLabel('sale_document_created','Dokument wydany'),
      accessor: 'sale_document_created',
      render: (row: OrderExtendedProfile) => <SaleDocumentCheckbox row={row} />,
      className: 'text-center w-[150px]'
    })

  existsColumnInList('total_cost') &&
    columns.push({
      header: getLabel('total_cost','Cena'),
      accessor: 'total_cost',
      render: (row: OrderExtendedProfile) => (
        <p className={row.paid_by_voucher ? ' text-sm' : ''}>
          {row.paid_by_voucher
            ? 'Bezpłatna Dieta'
            : formatCurrency(row.total_cost)}
        </p>
      ),
      className: 'text-center w-[80px] md:w-[100px]'
    })

  existsColumnInList('patient_profile') &&
    columns.push({
      header: getLabel('patient_profile','Przypisany kod'),
      accessor: 'user_assignment_code',
      render: (row: OrderExtendedProfile) => (
        <p className={'text-xs'}>{row?.patient_profile?.user_assignment_code ?? ''}</p>
      ),
      className: 'w-[120px]'
    })

  return columns
}

export const setOrdersTableActions = () => {
  const navigate = useNavigate()

  return [
    {
      label: 'Podgląd',
      icon: <CiSearch className="text-lg" />,
      onClick: (row: OrderExtendedProfile) => {
        navigate(`/orders/${row.id}/`)
      },
      className: 'text-blue-500'
    },
    {
      label: 'Idź do Profilu PD3',
      icon: <AiFillMedicineBox className="text-lg" />,
      onClick: (row: OrderExtendedProfile) => {
        const url = linkToProfilePD3(row.patient_profile?.pd3_uuid)
        if (row?.patient_profile?.pd3_uuid) window.open(url, '_blank')
      },
      className: 'text-orange-500',
      visible: (row: OrderExtendedProfile) => {
        return row.patient_profile?.pd3_uuid
      }
    }
  ]
}
